import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";
import LockIcon from "@mui/icons-material/Lock";
import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import * as yup from "yup";
import { useExtendedForm, routes } from "../common";
import { AuthContext } from "./useAuth";
import { AuthPage } from "./components";
import Divider from "@mui/material/Divider";

const useStyles = makeStyles()({
  rowLayout: {
    display: "flex",
    justifyContent: "space-between",
  },
  forgotPassword: {
    display: "inline-flex",
    alignItems: "center",
    marginBottom: 16,

    "& svg": {
      fontSize: "1em",
    },
  },
});

const initialValues = {
  email: "",
  password: "",
  remember: false,
};

const schema = yup
  .object<typeof initialValues>()
  .shape({
    email: yup.string().email().required("email is missing"),
    password: yup.string().required("password is missing"),
  })
  .defined();

const LoginPage: React.FC = () => {
  const { classes } = useStyles();
  const { login } = React.useContext(AuthContext);
  const location = useLocation();
  const search = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const loginHint = search.get("loginHint") || "";
  const formal = useExtendedForm({
    initialValues: { ...initialValues, email: loginHint },
    schema,
    onSubmit: (values) => login(values.email, values.password, values.remember),
  });

  return (
    <AuthPage>
      <form {...formal.getFormProps()} style={{ width: "100%" }}>
        <FormGroup>
          <Typography variant="h5" align="center">
            Sign in
          </Typography>
          <TextField
            label="Email address"
            type="email"
            {...formal.getTextFieldProps("email")}
          />
          <TextField
            label="Password"
            type="password"
            {...formal.getTextFieldProps("password")}
          />
          <div className={classes.rowLayout}>
            <FormControlLabel
              control={<Checkbox {...formal.getCheckboxProps("remember")} />}
              label="Remember me"
              slotProps={{
                typography: { variant: "body2" },
              }}
            />
            <Typography variant="body2" className={classes.forgotPassword}>
              <Link component={RouterLink} to={routes.FORGOT_PASSWORD.path}>
                <LockIcon /> Forgot password?
              </Link>
            </Typography>
          </div>
          <p>
            <Button
              variant="contained"
              size="large"
              color="primary"
              fullWidth
              {...formal.getSubmitButtonProps()}
              type="submit"
            >
              Log In
            </Button>
            {formal.submitState.state === "error" && (
              <FormHelperText error>
                {formal.submitState.message ||
                  "Invalid username or password. Please try again."}
              </FormHelperText>
            )}
          </p>
          <Typography align="center" sx={{ mt: 1, fontSize: 12 }}>
            Prefer passwordless login? {""}
            <Link
              component={RouterLink}
              sx={{ color: "#7d7d7d", textDecoration: "underline" }}
              to={
                formal.values.email
                  ? `${routes.MAGIC_TOKEN_LOGIN.path}?loginHint=${encodeURIComponent(
                      formal.values.email,
                    )}`
                  : routes.MAGIC_TOKEN_LOGIN.path
              }
            >
              Click here
            </Link>
          </Typography>
          <Divider sx={{ my: 4 }} />
          <Typography variant="body1" align="center">
            No account yet?{" "}
            <Link component={RouterLink} to={routes.REGISTER.path}>
              Sign Up
            </Link>
          </Typography>
        </FormGroup>
      </form>
    </AuthPage>
  );
};

export default LoginPage;
